import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Popover, Overlay, Button, Glyphicon } from '@bingads-webui-react/primitive';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { getStyles } from './getStyles';

class CalloutUnthemed extends React.Component {
  constructor(props) {
    super(props);

    this.attachRef = target => this.setState({ target });

    this.state = {
      uncontrolledShow: props.show === null ? false : null,
      enable: false,
    };

    this.hide = this.hide.bind(this);
    this.toggle = this.toggle.bind(this);
  }

  componentDidMount() {
    // dynamic styles are injected when mounting Callout.
    // To ensure overlay can correctly calculate position when mouting, delay render after mount.
    // this is to fix issue after upgrading to react 17
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      enable: true,
    });
  }

  toggle = () => {
    // If we are in controlled mode, do not update the state since
    // the show is being controlled by the caller.
    if (this.props.show === null) {
      this.setState(state => ({ uncontrolledShow: !state.uncontrolledShow }));
    }
  };

  hide = (e) => {
    // If we are in controlled mode, call onClose,
    // otherwise, hide the callout internally
    if (this.props.show !== null) {
      this.props.onClose(e);
    } else {
      this.setState({ uncontrolledShow: false });
    }
  };

  renderCloseButton() {
    return (
      <Button
        className={classNames([this.props.classes.calloutCloseButton, 'bingads-webui-react-callout-close-button'])}
        onClick={this.hide}
        aria-label={this.props.closeButtonAriaLabel}
      >
        <Glyphicon glyph="ba-Cancel" className={this.props.classes.calloutCloseButtonIcon} />
      </Button>
    );
  }

  renderChildWithClickOverride() {
    const childWithProps = React.cloneElement(React.Children.only(this.props.children), {
      onClick: this.toggle,
    });

    return childWithProps;
  }

  renderContent = () => (
    <React.Fragment>
      {this.props.content}
      {this.props.closeButton && this.renderCloseButton()}
    </React.Fragment>
  );

  render() {
    const { uncontrolledShow, target, enable } = this.state;

    const shouldShow = this.props.show !== null ? this.props.show : uncontrolledShow;

    // If we are uncontrolled, we need to render the child with the injected onClick handler.
    const child = this.props.show !== null
      ? this.props.children
      : this.renderChildWithClickOverride();

    const popoverClassNames = ['bingads-webui-react-callout-popover'];
    if (this.props.popoverClassName) {
      popoverClassNames.push(this.props.popoverClassName);
    }

    return (enable ?
      <React.Fragment>
        {/* Create a div here since we cannot pass a ref to the child since it may be stateless
          The div is styled so that it shrinks to its contents by default */}
        {this.props.target === null &&
          <div ref={this.attachRef} className={this.props.classes.calloutTargetContainer}>
            {child}
          </div>
        }
        <Overlay
          target={this.props.target || target}
          show={shouldShow}
          placement={this.props.placement}
          rootClose
          onHide={this.hide}
          container={this.props.container}
          shouldUpdatePosition={this.props.shouldUpdatePosition}
        >
          <Popover
            id={this.props.popoverId}
            title={this.props.title}
            className={classNames(popoverClassNames)}
            onClick={e => this.props.shouldStopPropagation && e.stopPropagation()}
          >
            { this.props.focusLockContent ? (
              <FocusLock
                focusFirstItemOnMount
                {...(this.props.focusLockProps ? this.props.focusLockProps : {})}
              >
                {this.renderContent()}
              </FocusLock>) : this.renderContent() }
          </Popover>
        </Overlay>
      </React.Fragment> : null
    );
  }
}

CalloutUnthemed.defaultProps = {
  placement: 'right',
  closeButton: true,
  title: undefined,
  content: undefined,
  popoverClassName: undefined,
  show: null,
  onClose: () => {},
  shouldStopPropagation: true,
  target: null,
  children: null,
  container: null,
  focusLockContent: false,
  focusLockProps: null,
  closeButtonAriaLabel: undefined,
  shouldUpdatePosition: false,
};

CalloutUnthemed.propTypes = {
  /**
   * the event target
   */
  target: PropTypes.instanceOf(Element),
  /**
   * the target element of the callout, it must take an onClick function
   */
  children: PropTypes.element,
  /**
   * a unique id for the popover, needed for accessibility
   */
  popoverId: PropTypes.string.isRequired,
  /**
   * adds the this class name to the popover if needed
   */
  popoverClassName: PropTypes.string,
  /**
   * whether to show the popover on render. If this is not set, the
   * callout will act in "uncontrolled" mode and manage its own show state.
   */
  show: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.oneOf([null]).isRequired,
  ]),
  /**
   * called when the callout wants to close from click outside or close button.
   * This is needed when show is set.
   */
  onClose: PropTypes.func,
  /**
   * the content element or text of the callout
   */
  content: PropTypes.oneOfType([
    PropTypes.element.isRequired,
    PropTypes.string.isRequired,
  ]),
  /**
   * the title element or text of the callout
   */
  title: PropTypes.oneOfType([
    PropTypes.element.isRequired,
    PropTypes.string.isRequired,
  ]),
  /**
   * the direction the callout is placed, adjacent to the child
   */
  placement: PropTypes.string,
  /**
   * shows the close button in the callout or not
   */
  closeButton: PropTypes.bool,
  /**
   * Prevents the click propagation to the parent, this is useful when the callout is controlled
   */
  shouldStopPropagation: PropTypes.bool,
  /**
   * class names that come from jss theme or default style
   */
  classes: PropTypes.shape({
    calloutCloseButton: PropTypes.string,
    calloutCloseButtonIcon: PropTypes.string,
    calloutTargetContainer: PropTypes.string,
  }).isRequired,
  /**
   * Specify container to control the DOM element to which to append the overlay.
   * This element must be a positioned element to allow correctly positioning the overlay.
   */
  container: PropTypes.shape(),
  /**
   * bool to enable/disable focus lock for the popover content.
   */
  focusLockContent: PropTypes.bool,
  /**
   * object to let user custom focus lock props outside.
   */
  focusLockProps: PropTypes.shape({}),
  /**
   * aria-label of close button
   */
  closeButtonAriaLabel: PropTypes.string,
  /**
   * should update position when content changes
   */
  shouldUpdatePosition: PropTypes.bool,
};

export const Callout = withDefaultStyles(CalloutUnthemed, getStyles);
